import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@utils/api';
import { toastManager } from './ToastContainer';

function DashboardSection() {
    const [stats, setStats] = useState({
        totalUsers: 0,
        vipUsers: 0,
        onlineNow: 0,
        pendingTransactions: 0
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStats = useCallback(async (showToast = false) => {
        try {
            if (showToast) setRefreshing(true);
            else setLoading(true);
            
            const token = localStorage.getItem('adminToken');
            
            // Parallel fetch for better performance
            const [usersRes, vipRes, transactionsRes] = await Promise.all([
                fetch(api('/api/admin/users?limit=100'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(api('/api/admin/vip/users?limit=1'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(api('/api/admin/vip/transactions?limit=1&status=pending'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            
            const usersData = usersRes.ok ? await usersRes.json() : null;
            const vipData = vipRes.ok ? await vipRes.json() : null;
            const transactionsData = transactionsRes.ok ? await transactionsRes.json() : null;
            
            // Count online users (those with is_online = true)
            const onlineCount = usersData?.users?.filter(u => u.is_online).length || 0;
            
            setStats({
                totalUsers: usersData?.pagination?.total || 0,
                vipUsers: vipData?.pagination?.total || 0,
                onlineNow: onlineCount,
                pendingTransactions: transactionsData?.pagination?.total || 0
            });
            
            if (showToast) {
                toastManager.success('Refreshed', 'Dashboard data updated');
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            if (showToast) {
                toastManager.error('Error', 'Failed to refresh stats');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, []);

    // Memoize stat cards to prevent re-rendering
    const statCards = useMemo(() => [
        { icon: 'fa-users', label: 'Total Users', value: stats.totalUsers, sublabel: 'Registered accounts', color: '#3b82f6' },
        { icon: 'fa-crown', label: 'VIP Members', value: stats.vipUsers, sublabel: 'Active subscriptions', color: '#d4af37' },
        { icon: 'fa-circle', label: 'Online Now', value: stats.onlineNow, sublabel: 'Currently online', color: '#10b981' },
        { icon: 'fa-clock', label: 'Pending VIP', value: stats.pendingTransactions, sublabel: 'Awaiting approval', color: '#f59e0b' }
    ], [stats]);

    if (loading) {
        return (
            <div className="admin-section">
                <div className="admin-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading dashboard stats...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 className="admin-section-title" style={{ margin: 0 }}>
                    <i className="fas fa-chart-bar"></i> Dashboard Overview
                </h2>
                <button 
                    className="admin-btn admin-btn-secondary" 
                    onClick={() => fetchStats(true)}
                    disabled={refreshing}
                >
                    <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i> 
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>
            
            <div className="stats-grid">
                {statCards.map((card, index) => (
                    <div className="stat-card" key={index}>
                        <div className="stat-card-header">
                            <div className="stat-icon" style={{ background: `${card.color}20`, color: card.color }}>
                                <i className={`fas ${card.icon}`}></i>
                            </div>
                            <div className="stat-info">
                                <h3>{card.label}</h3>
                                <p>{card.sublabel}</p>
                            </div>
                        </div>
                        <div className="stat-value" style={{ color: card.color }}>
                            {card.value.toLocaleString()}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Quick Actions */}
            <div className="quick-actions-section">
                <h3>
                    <i className="fas fa-bolt"></i> Quick Actions
                </h3>
                <div className="quick-actions-grid">
                    <button className="quick-action-btn" onClick={() => window.location.href = '#users'}>
                        <i className="fas fa-users"></i>
                        <span>Manage Users</span>
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '#vip'}>
                        <i className="fas fa-crown"></i>
                        <span>VIP Approvals</span>
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '#ads'}>
                        <i className="fas fa-ad"></i>
                        <span>Manage Ads</span>
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '#settings'}>
                        <i className="fas fa-cog"></i>
                        <span>Settings</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DashboardSection;

